<template>
  <Teleport to="body">
    <transition-group v-if="toastStore.toasts.length" name="toastsList" tag="ul" class="toast-list-wrapper">
      <li v-for="toast in toastStore.toasts" class="toast" :class="toast.type" :key="toast.id">
        <section class="toast-content">
          <span class="toast-icon" :class="getToastIconCssClass(toast.type)"></span>
          <div class="toast-text">
            <span v-if="toast.prefixText" class="prefix-text" v-html="toast.prefixText"></span>
            <span v-if="toast.itemName" class="item-name-wrapper">
              “<span class="item-name">{{ toast.itemName }}</span>”&nbsp;
            </span>
            <span v-if="toast.suffixText" class="suffix-text">{{ toast.suffixText }}&nbsp;</span>
            <span v-if="toast.newItemName" class="item-name-wrapper">
              “<span class="item-name">{{toast.newItemName}}</span>”
            </span>
          </div>
        </section>
        <span class="icon-close-cross-exit-quit-delete" @click="toastStore.removeToast(toast.id)"></span>
      </li>
    </transition-group>
  </Teleport>
</template>

<script setup lang="ts">
import { ToastType } from '@/models/toast.model';
import { useToastStore } from '@/store/toastStore';

const toastStore = useToastStore();

const getToastIconCssClass = (type: ToastType): string => {
  switch (type) {
    case ToastType.success:
      return 'icon-filled-circle-check';
    case ToastType.successfullyDeleted:
      return 'icon-trash';
    case ToastType.itemNotFound:
    case ToastType.error:
      return 'icon-triangle-exclamation-warning-alert';
    default:
      return '';
  }
};
</script>

<style>
:root {
  --toast-width: 570px;
  --toast-height: auto;
  --toast-min-height: 58px;
  --toast-max-height: 78px;
  --toast-padding: 8px 8px 8px 16px;
  --toast-close-icon-color: var(--colour-icons-neutral-default);
  --toast-border-radius: 8px;
  --toast-background-color: var(--colour-surface-overlay);
  --toast-text-color: var(--colour-text-neutral-subtle);
  --toast-font-size: var(--font-size-headers-medium);
  --toast-font-weight: var(--font-weight-body-medium);
  --toast-box-shadow: 0px 12px 32px -16px rgba(0, 0, 0, 0.05), 0px 8px 40px 0px rgba(0, 0, 0, 0.05);
  --toast-text-item-name-max-width: 145px;
  --toast-success-background-color: var(--colour-background-success-default);
  --toast-success-border-color: var(--colour-border-success-subtle);
  --toast-success-text-color: var(--colour-text-success-default);
  --toast-success-icon-color: var(--colour-icons-success-subtle);
  --toast-successfully-deleted-background-color: var(--colour-surface-overlay);
  --toast-successfully-deleted-border-color: var(--colour-border-neutral-subtle);
  --toast-successfully-deleted-text-color: var(--colour-text-danger-default);
  --toast-successfully-deleted-icon-color: var(--colour-text-danger-subtle);
  --toast-item-not-found-background-color: var(--colour-surface-overlay);
  --toast-item-not-found-border-color: var(--colour-border-neutral-subtle);
  --toast-item-not-found-text-color: var(--colour-text-neutral-default);
  --toast-item-not-found-icon-color: var(--colour-icons-warning-subtle);
  --toast-error-background-color: var(--colour-background-danger-default);
  --toast-error-border-color: var(--colour-border-neutral-subtle);
  --toast-error-text-color: var(--colour-text-danger-default);
  --toast-error-icon-color: var(--colour-icons-danger-subtle);
}
</style>

<style lang="scss" scoped>
.toast-list-wrapper {
  position: fixed;
  top: 24px;
  right: 24px;
  z-index: 2800;
  display: flex;
  flex-direction: column;
  gap: 5px;
  margin: 0;

  .toast {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 16px;
    width: var(--toast-width);
    height: var(--toast-height);
    min-height: var(--toast-min-height);
    max-height: var(--toast-max-height);
    padding: var(--toast-padding);
    border-radius: var(--toast-border-radius);
    box-shadow: var(--toast-box-shadow);
    background-color: var(--toast-background-color);
    border: 1px solid transparent;

    .toast-content {
      display: flex;
      align-items: center;
      gap: 16px;
      width: 100%;
      overflow: hidden;
    }

    .toast-icon {
      font-size: 20px;
    }

    .toast-text {
      display: inline-flex;
      font-size: var(--toast-font-size);
      font-weight: var(--toast-font-weight);
      flex-wrap: wrap;

      .item-name-wrapper {
        display: flex;

        .item-name {
          font-weight: var(--font-weight-body-medium);
          max-width: var(--toast-text-item-name-max-width);
          text-wrap: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }
      }

      .prefix-text,
      .suffix-text {
        text-wrap: wrap;
      }
    }

    &.success {
      background-color: var(--toast-success-background-color);
      border-color: var(--toast-success-border-color);
      color: var(--toast-success-text-color);

      .toast-icon {
        color: var(--toast-success-icon-color);
      }
    }

    &.successfully-deleted {
      background-color: var(--toast-successfully-deleted-background-color);
      border-color: var(--toast-successfully-deleted-border-color);
      color: var(--toast-successfully-deleted-text-color);

      .toast-icon {
        color: var(--toast-successfully-deleted-icon-color);
      }
    }

    &.item-not-found {
      background-color: var(--toast-item-not-found-background-color);
      border-color: var(--toast-item-not-found-border-color);
      color: var(--toast-item-not-found-text-color);

      .toast-icon {
        color: var(--toast-item-not-found-icon-color);
      }
    }

    &.error {
      background-color: var(--toast-error-background-color);
      border-color: var(--toast-error-border-color);
      color: var(--toast-error-text-color);

      .toast-icon {
        color: var(--toast-error-icon-color);
      }
    }

    .icon-close-cross-exit-quit-delete {
      font-size: 32px;
      color: var(--toast-close-icon-color);
      margin: 0;
      cursor: pointer;
    }
  }
}
</style>
